import { createMachine, actions } from 'xstate';
import type { KeyboardEvent } from 'react';
import invariant from 'tiny-invariant';
import type { OptionParams } from './internal/types';

const { assign, choose, raise } = actions;

export type Message =
  | {
      type: 'focus' | 'blur' | 'change' | 'submit' | 'toggle_history' | 'click';
    }
  | { type: 'select' | 'delete' | 'follow_link'; id: OptionParams['id'] }
  | {
      type: 'register_option';
      params: OptionParams;
    }
  | {
      type: 'unregister_option';
      id: OptionParams['id'];
    }
  | (KeyboardEvent & { type: 'keydown' });

export type MachineContext = {
  options: OptionParams[];
} & (
  | {
      activeRow: OptionParams;
      activeDescendant: OptionParams['cellIds'][number];
    }
  | { activeRow: undefined; activeDescendant: undefined }
);

function isArrowUp(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'ArrowUp';
}
function isArrowDown(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'ArrowDown';
}
function isArrowLeft(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'ArrowLeft';
}
function isArrowRight(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'ArrowRight';
}
function isEnter(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'Enter';
}
function isEscape(_: MachineContext, message: KeyboardEvent) {
  return message.key === 'Escape';
}

export const searchMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Это требуется для корреткной типизации
    tsTypes: {} as import('./search.machine.typegen').Typegen0,
    schema: {
      events: {} as Message,
      context: {} as MachineContext,
    },
    id: 'searchboxMachine',
    predictableActionArguments: true,
    preserveActionOrder: true,
    context: {
      options: [],
      activeRow: undefined,
      activeDescendant: undefined,
    },
    on: {
      register_option: {
        internal: true,
        actions: ['register_option'],
      },
      unregister_option: [
        {
          internal: true,
          actions: ['unregister_option'],
        },
      ],
    },
    initial: 'closed',
    states: {
      closed: {
        on: {
          click: {
            target: 'open',
            cond: 'have_options',
          },
          focus: {
            target: 'open',
            cond: 'have_options',
          },
          keydown: {
            target: 'open',
            cond: 'have_options',
          },
          change: {
            target: 'open',
            cond: 'have_options',
          },
        },
      },
      open: {
        initial: 'list_unfocused',
        on: {
          blur: { target: 'closed' },
          keydown: { target: 'closed', cond: 'is_escape' },
          change: { target: '.list_unfocused' },
          select: {
            target: 'closed',
            actions: 'select_option',
          },
          submit: {
            target: 'closed',
            actions: ['submit'],
          },
          delete: { internal: true, actions: ['delete'] },
          toggle_history: { internal: true, actions: ['toggle_history'] },
        },
        states: {
          list_unfocused: {
            entry: ['reset_selection'],
            on: {
              keydown: [
                {
                  target: 'list_focused',
                  cond: 'is_arrow_down',
                  actions: ['select_next_option', 'prevent_default'],
                },
                {
                  target: 'list_focused',
                  cond: 'is_arrow_up',
                  actions: ['select_prev_option', 'prevent_default'],
                },
                {
                  target: '#searchboxMachine.closed',
                  cond: 'is_enter',
                  actions: ['submit', 'prevent_default'],
                },
              ],
            },
          },
          list_focused: {
            on: {
              unregister_option: {
                target: 'list_unfocused',
                cond: 'unrgistering_last_option',
                actions: ['unregister_option'],
              },
              keydown: [
                {
                  target: 'list_unfocused',
                  cond: 'should_unfocus_list',
                  actions: ['prevent_default'],
                },
                {
                  internal: true,
                  actions: ['select_next_option', 'prevent_default'],
                  cond: 'is_arrow_down',
                },
                {
                  internal: true,
                  actions: ['select_prev_option', 'prevent_default'],
                  cond: 'is_arrow_up',
                },
                {
                  internal: true,
                  actions: ['select_next_cell', 'prevent_default'],
                  cond: 'is_arrow_right',
                },
                {
                  internal: true,
                  actions: ['select_prev_cell', 'prevent_default'],
                  cond: 'is_arrow_left',
                },
                {
                  internal: true,
                  actions: ['perform_action', 'prevent_default'],
                  cond: 'is_enter',
                },
              ],
            },
          },
        },
      },
    },
  },
  {
    guards: {
      have_options(ctx, message) {
        if (message.type === 'focus' || message.type === 'click') {
          return ctx.options.length > 0;
        }
        return (
          message.type === 'keydown' &&
          (isArrowDown(ctx, message) || isEnter(ctx, message)) &&
          ctx.options.length > 0
        );
      },
      is_escape: isEscape,
      is_enter: isEnter,
      is_arrow_up: isArrowUp,
      is_arrow_down: isArrowDown,
      is_arrow_right: isArrowRight,
      is_arrow_left: isArrowLeft,
      should_unfocus_list(ctx, message) {
        const isFirstRowSelected = ctx.activeRow === ctx.options[0];
        if (isFirstRowSelected && message.key === 'ArrowUp') {
          return true;
        }
        return false;
      },
      unrgistering_last_option(ctx) {
        return ctx.options.length === 1;
      },
      nothing_selected(ctx) {
        return ctx.activeRow === undefined;
      },
      last_cell_selected(ctx) {
        return ctx.activeRow?.cellIds.indexOf(ctx.activeDescendant) === 1;
      },
      first_cell_selected(ctx) {
        return ctx.activeRow?.cellIds.indexOf(ctx.activeDescendant) === 0;
      },
      on_row_with_single_cell(ctx) {
        return ctx.activeRow?.cellIds.length === 1;
      },
      is_submit_option(ctx) {
        return ctx.activeRow?.type === 'submit';
      },
      is_delete_from_history(ctx) {
        return (
          ctx.activeRow?.type === 'actionable' &&
          ctx.activeRow.cellIds[1] === ctx.activeDescendant
        );
      },
      is_history_toggle(ctx) {
        return ctx.activeRow?.type === 'history_toggle';
      },
    },
    actions: {
      register_option: assign({
        options(ctx, message) {
          return ctx.options.concat(message.params);
        },
      }),
      unregister_option: assign({
        options(ctx, message) {
          return ctx.options.filter((option) => option.id !== message.id);
        },
      }),
      prevent_default(_, message) {
        message.preventDefault();
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Нужно для корректной генерации типов
      reset_selection: assign((_) => ({
        activeRow: undefined,
        activeDescendant: undefined,
      })),
      select_next_option: choose([
        {
          cond: 'nothing_selected',
          actions: ['select_first_row', 'select_first_cell'],
        },
        {
          cond: 'first_cell_selected',
          actions: ['select_next_row', 'select_first_cell'],
        },
        {
          cond: 'last_cell_selected',
          actions: ['select_next_row', 'select_last_cell'],
        },
      ]),
      select_prev_option: choose([
        {
          cond: 'nothing_selected',
          actions: ['select_last_row', 'select_first_cell'],
        },
        {
          cond: 'first_cell_selected',
          actions: ['select_prev_row', 'select_first_cell'],
        },
        {
          cond: 'last_cell_selected',
          actions: ['select_prev_row', 'select_last_cell'],
        },
      ]),
      select_first_row: assign({
        activeRow(ctx) {
          return ctx.options[0];
        },
      }),
      select_last_row: assign({
        activeRow(ctx) {
          return ctx.options[ctx.options.length - 1];
        },
      }),
      select_next_row: assign({
        activeRow(ctx) {
          const firstOption = ctx.options[0];
          if (!ctx.activeRow) return firstOption;
          const nextRow = ctx.options[ctx.options.indexOf(ctx.activeRow) + 1];
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Тайпскрипт ошибается. Поскольку мы не знаем количество элементов в массиве, мы не можем быть уверены что +1 не отдаст нам undefined
          return nextRow || firstOption;
        },
      }),
      select_prev_row: assign({
        activeRow(ctx) {
          const lastOption = ctx.options[ctx.options.length - 1];
          if (!ctx.activeRow) return lastOption;
          const nextRow = ctx.options[ctx.options.indexOf(ctx.activeRow) - 1];
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Тайпскрипт ошибается. Поскольку мы не знаем количество элементов в массиве, мы не можем быть уверены что -1 не отдаст нам undefined
          return nextRow || lastOption;
        },
      }),
      select_first_cell: assign({
        activeDescendant(ctx) {
          invariant(ctx.activeRow !== undefined);
          return ctx.activeRow.cellIds[0];
        },
      }),
      select_last_cell: assign({
        activeDescendant(ctx: MachineContext) {
          invariant(ctx.activeRow !== undefined);
          return ctx.activeRow.cellIds[1] ?? ctx.activeRow.cellIds[0];
        },
      }),
      select_next_cell: choose([
        {
          cond: 'on_row_with_single_cell',
          actions: ['select_next_row', 'select_first_cell'],
        },
        {
          cond: 'last_cell_selected',
          actions: ['select_next_row', 'select_first_cell'],
        },
        { cond: 'first_cell_selected', actions: ['select_last_cell'] },
      ]),
      select_prev_cell: choose([
        {
          cond: 'on_row_with_single_cell',
          actions: ['select_prev_row', 'select_last_cell'],
        },
        { cond: 'last_cell_selected', actions: ['select_first_cell'] },
        {
          cond: 'first_cell_selected',
          actions: ['select_prev_row', 'select_last_cell'],
        },
      ]),
      perform_action: choose([
        { cond: 'is_submit_option', actions: raise('submit') },
        { cond: 'is_delete_from_history', actions: raise('delete') },
        { cond: 'is_history_toggle', actions: raise('toggle_history') },
        { actions: raise('select') },
      ]),
    },
  }
);
